import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bot, Send, User, Sparkles, TrendingUp, AlertTriangle, ShieldCheck,
  Lightbulb, FileText, DollarSign, BarChart3, Clock, Trash2, RotateCcw,
  Brain, Zap, MessageSquare, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAccounting } from "@/contexts/AccountingContext";
import { useExpenses } from "@/contexts/ExpenseContext";
import { useCompany } from "@/contexts/CompanyContext";
import { processChat, ChatMessage, detectInvoiceRisks, generateForecasts, InvoiceRisk, Forecast } from "@/lib/ai/engine";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const QUICK_QUESTIONS = [
  { icon: TrendingUp, label: "What's my profit?", q: "What is my profit this month?" },
  { icon: FileText, label: "Unpaid invoices", q: "Which clients have unpaid invoices?" },
  { icon: DollarSign, label: "Show expenses", q: "Show expenses for last quarter" },
  { icon: ShieldCheck, label: "GST owed", q: "How much GST do I owe?" },
  { icon: BarChart3, label: "Cash flow", q: "What's my cash flow position?" },
  { icon: Clock, label: "Pending approvals", q: "Any pending approvals?" },
];

const fmt = (n: number) => `₹${n.toLocaleString("en-IN")}`;

// Simple markdown-like renderer
function RenderMessage({ content }: { content: string }) {
  const lines = content.split("\n");
  return (
    <div className="space-y-1 text-sm leading-relaxed">
      {lines.map((line, i) => {
        if (line.startsWith("| ") && line.includes("|")) {
          const cells = line.split("|").filter(Boolean).map((c) => c.trim());
          if (cells.every((c) => c.match(/^-+$/))) return null;
          const isHeader = i < lines.length - 1 && lines[i + 1]?.includes("---");
          return (
            <div key={i} className={`grid gap-2 px-2 py-1.5 rounded text-xs ${isHeader ? "bg-muted/50 font-semibold" : "border-b border-border/30"}`} style={{ gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}>
              {cells.map((c, ci) => <span key={ci} className={ci > 0 ? "text-right" : ""}>{c.replace(/\*\*/g, "")}</span>)}
            </div>
          );
        }
        if (line.startsWith("- ")) {
          return <div key={i} className="flex gap-2 text-xs pl-2"><span className="text-accent">•</span><span dangerouslySetInnerHTML={{ __html: line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} /></div>;
        }
        const formatted = line
          .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
          .replace(/^#{1,3}\s/, "");
        if (!line.trim()) return <div key={i} className="h-1" />;
        return <p key={i} className="text-muted-foreground" dangerouslySetInnerHTML={{ __html: formatted }} />;
      })}
    </div>
  );
}

// Risk Alert Card
function RiskCard({ risk }: { risk: InvoiceRisk }) {
  const colors = {
    high: "border-destructive/30 bg-destructive/5",
    medium: "border-warning/30 bg-warning/5",
    low: "border-accent/30 bg-accent/5",
  };
  const badges = {
    high: "bg-destructive/10 text-destructive",
    medium: "bg-warning/10 text-warning",
    low: "bg-accent/10 text-accent",
  };
  return (
    <div className={`p-3 rounded-lg border ${colors[risk.severity]}`}>
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className={`h-3.5 w-3.5 ${risk.severity === "high" ? "text-destructive" : risk.severity === "medium" ? "text-warning" : "text-accent"}`} />
        <span className="text-xs font-semibold text-foreground">{risk.title}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badges[risk.severity]}`}>{risk.severity}</span>
      </div>
      <p className="text-xs text-muted-foreground">{risk.description}</p>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs font-medium text-foreground">{risk.partyName} — {fmt(risk.amount)}</span>
        <span className="text-[10px] text-accent">{risk.recommendation}</span>
      </div>
    </div>
  );
}

// Forecast Card
function ForecastCard({ forecast }: { forecast: Forecast }) {
  return (
    <div className="p-4 rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground">{forecast.metric}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${forecast.trend === "up" ? "bg-success/10 text-success" : forecast.trend === "down" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`}>
          {forecast.trend === "up" ? "↑" : forecast.trend === "down" ? "↓" : "→"} {Math.abs(forecast.changePercent).toFixed(1)}%
        </span>
      </div>
      <div className="flex items-baseline gap-3 mb-1">
        <span className="text-lg font-bold text-foreground">{fmt(forecast.projectedValue)}</span>
        <span className="text-xs text-muted-foreground">from {fmt(forecast.currentValue)}</span>
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug">{forecast.description}</p>
      <div className="flex items-center gap-1 mt-2">
        <div className="h-1 rounded-full bg-muted flex-1">
          <div className="h-1 rounded-full bg-accent" style={{ width: `${forecast.confidence * 100}%` }} />
        </div>
        <span className="text-[10px] text-muted-foreground">{(forecast.confidence * 100).toFixed(0)}% confidence</span>
      </div>
    </div>
  );
}

const AIAssistantPage = () => {
  const { vouchers, ledgers } = useAccounting();
  const { expenses } = useExpenses();
  const { activeCompany } = useCompany();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const risks = detectInvoiceRisks(vouchers, ledgers);
  const forecasts = generateForecasts(vouchers, ledgers);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim()) return;
    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: "user", content: text, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);

    try {
      const res = await fetch("/api/ai/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: text,
          companyId: activeCompany?.id || null,
          history: messages.slice(-4),
        }),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || "AI service is currently unavailable. Please try again later.");
      }

      const aiMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: json.data.reply,
        timestamp: new Date().toISOString(),
        sources: json.data.sources || ["General Ledger", "Voucher Records"],
      };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `⚠️ **AI Service Notice:** ${err.message || "The AI model is currently unreachable. Please try again in a few moments."}`,
        timestamp: new Date().toISOString(),
        sources: ["Service Status"],
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
    }
  }, [activeCompany, messages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  return (
    <div className="space-y-4 h-[calc(100vh-120px)]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Brain className="h-6 w-6 text-accent" />
            AccouPro AI
          </h1>
          <p className="text-sm text-muted-foreground">Your intelligent accounting assistant</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-accent font-medium bg-accent/10 px-2 py-1 rounded-full flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> AI Powered
          </span>
        </div>
      </div>

      <Tabs defaultValue="chat" className="flex-1">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="chat" className="gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Chat</TabsTrigger>
          <TabsTrigger value="risks" className="gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Risk Alerts ({risks.length})</TabsTrigger>
          <TabsTrigger value="forecast" className="gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> Forecasting</TabsTrigger>
          <TabsTrigger value="gst" className="gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> GST Advisor</TabsTrigger>
        </TabsList>

        {/* CHAT TAB */}
        <TabsContent value="chat" className="flex flex-col h-[calc(100vh-260px)]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pr-2 pb-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-4">
                  <Bot className="h-8 w-8 text-accent" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">How can I help you today?</h3>
                <p className="text-sm text-muted-foreground mb-6">Ask me anything about your company's finances</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-w-xl">
                  {QUICK_QUESTIONS.map((qq) => (
                    <motion.button
                      key={qq.q}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => sendMessage(qq.q)}
                      className="flex items-center gap-2 p-3 rounded-lg border border-border bg-card hover:border-accent/30 hover:bg-accent/5 transition-all text-left"
                    >
                      <qq.icon className="h-4 w-4 text-accent flex-shrink-0" />
                      <span className="text-xs text-foreground">{qq.label}</span>
                    </motion.button>
                  ))}
                </div>
              </div>
            ) : (
              <AnimatePresence>
                {messages.map((msg) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}
                  >
                    {msg.role === "assistant" && (
                      <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Bot className="h-4 w-4 text-accent" />
                      </div>
                    )}
                    <div className={`max-w-[80%] rounded-xl p-4 ${msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                      {msg.role === "user" ? (
                        <p className="text-sm">{msg.content}</p>
                      ) : (
                        <RenderMessage content={msg.content} />
                      )}
                      {msg.sources && (
                        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border/30">
                          <span className="text-[10px] text-muted-foreground">Sources:</span>
                          {msg.sources.map((s) => (
                            <span key={s} className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {msg.role === "user" && (
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            )}

            {isTyping && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="h-4 w-4 text-accent" />
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-accent/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 rounded-full bg-accent/40 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 rounded-full bg-accent/40 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Input area */}
          <div className="border-t border-border pt-3 mt-auto">
            <div className="flex gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask AccouPro AI anything about your finances..."
                className="min-h-[44px] max-h-[120px] resize-none"
                rows={1}
              />
              <Button onClick={() => sendMessage(input)} disabled={!input.trim() || isTyping} size="icon" className="h-[44px] w-[44px] flex-shrink-0">
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[10px] text-muted-foreground">AI responses are based on your accounting data. Verify critical information.</span>
              {messages.length > 0 && (
                <button onClick={() => setMessages([])} className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1">
                  <Trash2 className="h-3 w-3" /> Clear chat
                </button>
              )}
            </div>
          </div>
        </TabsContent>

        {/* RISK ALERTS TAB */}
        <TabsContent value="risks" className="space-y-3 max-h-[calc(100vh-260px)] overflow-y-auto">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-medium bg-destructive/10 text-destructive px-2 py-1 rounded-full">
              {risks.filter((r) => r.severity === "high").length} High
            </span>
            <span className="text-xs font-medium bg-warning/10 text-warning px-2 py-1 rounded-full">
              {risks.filter((r) => r.severity === "medium").length} Medium
            </span>
            <span className="text-xs font-medium bg-accent/10 text-accent px-2 py-1 rounded-full">
              {risks.filter((r) => r.severity === "low").length} Low
            </span>
          </div>
          {risks.length === 0 ? (
            <div className="text-center py-12">
              <ShieldCheck className="h-10 w-10 text-success mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No risk alerts detected. Your invoices look healthy!</p>
            </div>
          ) : (
            risks.map((risk) => <RiskCard key={risk.id} risk={risk} />)
          )}
        </TabsContent>

        {/* FORECASTING TAB */}
        <TabsContent value="forecast" className="space-y-3 max-h-[calc(100vh-260px)] overflow-y-auto">
          <p className="text-xs text-muted-foreground">Projections based on historical data patterns and seasonal trends</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {forecasts.map((fc) => <ForecastCard key={fc.id} forecast={fc} />)}
          </div>
        </TabsContent>

        {/* GST ADVISOR TAB */}
        <TabsContent value="gst" className="space-y-4 max-h-[calc(100vh-260px)] overflow-y-auto">
          <div className="p-4 rounded-lg bg-card border border-border">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-5 w-5 text-accent" />
              <h3 className="text-sm font-semibold text-foreground">GST Compliance Advisor</h3>
            </div>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-accent/5 border border-accent/20">
                <div className="flex items-center gap-2 mb-1">
                  <Lightbulb className="h-3.5 w-3.5 text-accent" />
                  <span className="text-xs font-semibold text-foreground">Filing Reminder</span>
                </div>
                <p className="text-xs text-muted-foreground">GSTR-1 for the current period is due on the 11th. GSTR-3B is due on the 20th. Ensure all sales invoices are recorded.</p>
              </div>

              <div className="p-3 rounded-lg bg-warning/5 border border-warning/20">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                  <span className="text-xs font-semibold text-foreground">ITC Verification Needed</span>
                </div>
                <p className="text-xs text-muted-foreground">Cross-verify your purchase invoices with GSTR-2B before claiming Input Tax Credit. Use the GST Reconciliation module for automated matching.</p>
              </div>

              <div className="p-3 rounded-lg bg-success/5 border border-success/20">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="h-3.5 w-3.5 text-success" />
                  <span className="text-xs font-semibold text-foreground">E-Invoice Compliance</span>
                </div>
                <p className="text-xs text-muted-foreground">Businesses with turnover above ₹5 Cr must generate e-invoices. Use the E-Invoice module for automatic IRN generation.</p>
              </div>

              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center gap-2 mb-1">
                  <Brain className="h-3.5 w-3.5 text-accent" />
                  <span className="text-xs font-semibold text-foreground">HSN Code Validator</span>
                </div>
                <p className="text-xs text-muted-foreground">AccouPro AI validates HSN/SAC codes against GST rate schedules in real-time during invoice creation. Incorrect rates are flagged automatically.</p>
              </div>

              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="h-3.5 w-3.5 text-accent" />
                  <span className="text-xs font-semibold text-foreground">Reverse Charge Mechanism</span>
                </div>
                <p className="text-xs text-muted-foreground">AI monitors transactions that may attract RCM (imports, unregistered vendor purchases) and alerts you to self-assess GST liability.</p>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIAssistantPage;
